import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { AccountSelect } from "@/components/AccountSelect";
import { api, errMsg } from "@/lib/api";
import { activeSessions, sessionByKey } from "@/lib/trading";

const nowUtcInput = () => new Date().toISOString().slice(0, 16);
const toInput = (iso) => (iso ? new Date(iso).toISOString().slice(0, 16) : "");
const empty = (accountId) => ({ account_id: accountId || "", symbol: "", market: "Forex", side: "buy", lots: "", entry_price: "", exit_price: "", entry_time: nowUtcInput(), exit_time: "", pnl: "", notes: "" });

const Field = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs text-slate-400">{label}</Label>{children}</div>;

export const TradeDialog = ({ open, onOpenChange, accounts, trade, defaultAccount, onSaved }) => {
  const [f, setF] = useState(empty());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF(trade ? { ...trade, exit_price: trade.exit_price ?? "", entry_time: toInput(trade.entry_time), exit_time: toInput(trade.exit_time) } : empty(defaultAccount || accounts[0]?.id));
  }, [open, trade, defaultAccount, accounts]);

  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const detected = f.entry_time ? activeSessions(new Date(`${f.entry_time}:00Z`)) : [];

  const save = async () => {
    if (!f.account_id || !f.symbol || !f.lots || f.entry_price === "" || f.pnl === "") return toast.error("Fill in account, symbol, lots, entry price and P&L");
    setSaving(true);
    const body = {
      account_id: f.account_id, symbol: f.symbol, market: f.market, side: f.side, notes: f.notes,
      lots: Number(f.lots), entry_price: Number(f.entry_price), pnl: Number(f.pnl),
      exit_price: f.exit_price === "" ? null : Number(f.exit_price),
      entry_time: `${f.entry_time}:00Z`, exit_time: f.exit_time ? `${f.exit_time}:00Z` : null,
    };
    try {
      if (trade) await api.put(`/trades/${trade.id}`, body);
      else await api.post("/trades", body);
      toast.success(trade ? "Trade updated" : "Trade logged");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121722] border-slate-800 max-w-xl max-h-[90vh] overflow-y-auto" data-testid="trade-dialog">
        <DialogHeader><DialogTitle className="font-display">{trade ? "Edit trade" : "Log a trade"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><Field label="Account"><AccountSelect accounts={accounts} value={f.account_id} onChange={set("account_id")} testId="trade-account-select" /></Field></div>
          <Field label="Symbol"><Input data-testid="trade-symbol-input" value={f.symbol} onChange={set("symbol")} placeholder="EURUSD" className="uppercase font-mono" /></Field>
          <Field label="Market">
            <Select value={f.market} onValueChange={set("market")}>
              <SelectTrigger data-testid="trade-market-select"><SelectValue /></SelectTrigger>
              <SelectContent>{["Forex", "Metals", "Indices", "Crypto", "Commodities"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Side">
            <Select value={f.side} onValueChange={set("side")}>
              <SelectTrigger data-testid="trade-type-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="buy">Buy / Long</SelectItem><SelectItem value="sell">Sell / Short</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="Lots"><Input data-testid="trade-lot-input" type="number" step="0.01" value={f.lots} onChange={set("lots")} className="font-mono" /></Field>
          <Field label="Entry price"><Input data-testid="trade-entry-price-input" type="number" step="any" value={f.entry_price} onChange={set("entry_price")} className="font-mono" /></Field>
          <Field label="Exit price"><Input data-testid="trade-exit-price-input" type="number" step="any" value={f.exit_price} onChange={set("exit_price")} className="font-mono" /></Field>
          <Field label="Entry time (UTC)"><Input data-testid="trade-entry-time-input" type="datetime-local" value={f.entry_time} onChange={set("entry_time")} className="font-mono" /></Field>
          <Field label="Exit time (UTC)"><Input data-testid="trade-exit-time-input" type="datetime-local" value={f.exit_time} onChange={set("exit_time")} className="font-mono" /></Field>
          <div className="col-span-2"><Field label="Net P&L ($)"><Input data-testid="trade-pnl-input" type="number" step="0.01" value={f.pnl} onChange={set("pnl")} placeholder="e.g. 125.50 or -40" className="font-mono" /></Field></div>
          <div className="col-span-2 flex flex-wrap items-center gap-2 text-xs" data-testid="trade-detected-sessions">
            <span className="text-slate-500 font-mono">Session(s) at entry:</span>
            {detected.length ? detected.map((k) => <span key={k} className={`px-2 py-0.5 rounded border font-mono ${sessionByKey[k].badge}`}>{sessionByKey[k].label}</span>) : <span className="text-slate-500 font-mono">Off-session (21–24 UTC)</span>}
          </div>
          <div className="col-span-2"><Field label="Notes"><Textarea data-testid="trade-notes-input" value={f.notes} onChange={set("notes")} rows={2} /></Field></div>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-trade-button" className="w-full mt-2 bg-emerald-600 hover:bg-emerald-500 text-white">{saving ? "Saving…" : "Save trade"}</Button>
      </DialogContent>
    </Dialog>
  );
};
